import {FormInstance} from 'antd/es/form';
import {isNil} from 'lodash-es';

import {TEditableColumn, TFieldStateType} from 'components/editable-table/index';
import {executeFormWatchers} from 'components/editable-table/lib';
import {DEFAULT_STATE_CELL, getDefaultFieldState} from 'components/editable-table/ui/editable-cell/editable-cell';
import {EFormMode} from 'components/form';
import {IFieldMeta, IValidationRules} from 'entities/metadata';
import {TEntity, TEntityValue} from 'modules/data';
import {isArray, isString} from 'shared/utils';

export enum EEditableCellMaskValidation {
    sumOfFields = 'sumOfFields',
    checkMore = 'checkMore',
    checkEqual = 'checkEqual',
    checkNumericalValue = 'checkNumericalValue',
    checkIntegerValue = 'checkIntegerValue',
    checkSum = 'checkSum',
    // Используется, когда у поля нет поля для ввода, а ввод данных происходит при помощи другой формы
    checkValueSet = 'checkValueSet',
    requestValidation = 'requestValidation',
    errorWatcher = 'errorWatcher',
    greaterOrEqual = 'greaterOrEqual',
}

const DEFAULT_ERROR_MESSAGE = 'Неверное значение';
const REQUIRED_FIELD_ERROR_MESSAGE = 'Поле должно быть заполнено';

/**
 * Асинхронно валидирует значение поля на основе правил валидации
 *
 * @template TEntity - Тип сущности, представляющей запись данных
 * @template TEntityValue - Тип значения поля (string, number, boolean и т.д.)
 * @param value - Значение поля для валидации
 * @param cellState - Состояние ячейки с мета-информацией (активация, обязательность, зависимости)
 * @param record - Исходная запись данных (может быть использована для кросс-полейных проверок)
 * @param validationRules - Массив правил валидации для применения к полю
 * @param isRequired - Флаг обязательности поля (переопределяет cellState.required)
 * @param dataIndex - Имя/ключ поля в данных (используется для специфических проверок)
 * @param rowData - Альтернативный источник данных строки (используется вместо record если передан)
 * @returns Промис с массивом сообщений об ошибках валидации. Пустой массив если ошибок нет
 *
 * @throws {Error} При внутренних ошибках выполнения правил валидации
 *
 * @see EEditableCellMaskValidation - Перечисление доступных масок валидации
 * @see validateRowFull - Функция валидации всей строки, использующая эту функцию
 */
const validateFieldValue = async (
    value: TEntityValue,
    cellState: TFieldStateType,
    record: TEntity | undefined,
    validationRules: IValidationRules[],
    isRequired: boolean | undefined,
    dataIndex: string,
    rowData?: TEntity,
): Promise<string[]> => {
    const errors: string[] = [];
    const {
        activated,
        required,
        dependencies,
    } = cellState;

    const isFieldRequired = required || isRequired;

    const rules = [...validationRules];

    if (isFieldRequired && activated) {
        rules.push({
            mask: EEditableCellMaskValidation.checkValueSet,
            errorMessage: REQUIRED_FIELD_ERROR_MESSAGE,
        });
    }

    for (const item of rules) {
        const {
            mask,
            errorMessage,
            regExp,
            minNumericalValue,
            maxNumericalValue,
        } = item;

        if (mask === EEditableCellMaskValidation.sumOfFields) {
            const mainValue = Number(value);
            let dependenciesValue = 0;

            if (isArray(dependencies)) {
                dependencies.forEach(depend => {
                    dependenciesValue += Number(rowData?.[depend] ?? record?.[depend]);
                });
            }
            if (mainValue !== dependenciesValue) {
                errors.push(errorMessage || DEFAULT_ERROR_MESSAGE);
            }
        }

        if (mask === EEditableCellMaskValidation.checkMore) {
            const mainValue = Number(value);
            let dependsValue: number = 0;

            if (isString(dependencies)) {
                dependsValue += Number(rowData?.[dependencies] ?? record?.[dependencies]);
            }
            if (mainValue < dependsValue) {
                errors.push(errorMessage || DEFAULT_ERROR_MESSAGE);
            }
        }

        if (mask === EEditableCellMaskValidation.checkEqual) {
            const mainValue = Number(value);
            let dependsValue: number = 0;

            if (value !== '' && mainValue === 0) {
                errors.push(errorMessage || DEFAULT_ERROR_MESSAGE);
            }
            if (isString(dependencies)) {
                dependsValue += Number(rowData?.[dependencies] ?? record?.[dependencies]);
            }
            if (mainValue > dependsValue) {
                errors.push(errorMessage || DEFAULT_ERROR_MESSAGE);
            }
        }

        if (mask === EEditableCellMaskValidation.checkNumericalValue) {
            const numValue = Number(value);

            if (!isNil(minNumericalValue) && numValue < minNumericalValue) {
                errors.push(errorMessage ?? DEFAULT_ERROR_MESSAGE);
            }
            if (!isNil(maxNumericalValue) && numValue > maxNumericalValue) {
                errors.push(errorMessage ?? DEFAULT_ERROR_MESSAGE);
            }
        }

        if (mask === EEditableCellMaskValidation.checkIntegerValue) {
            if (value && !/^-?\d+$/.test(value.toString())) {
                errors.push(errorMessage ?? DEFAULT_ERROR_MESSAGE);
            }
        }

        else if (mask === EEditableCellMaskValidation.checkSum) {
            if (typeof value === 'number' && value >= 0) {
                if (Number(value) !== Number(rowData?.failedCP || 0) + Number(rowData?.successCP || 0)) {
                    errors.push(errorMessage ?? DEFAULT_ERROR_MESSAGE);
                }
            }
        }

        if (mask === EEditableCellMaskValidation.checkValueSet) {
            if (isFieldRequired && (isNil(value) || value === '')) {
                errors.push(errorMessage ?? REQUIRED_FIELD_ERROR_MESSAGE);
            }
        }

        if (mask === EEditableCellMaskValidation.greaterOrEqual) {
            if (dataIndex === 'operationsSum' && (rowData?.operationsSum || 0) < (rowData?.operationsChecked || 0)) {
                errors.push(errorMessage ?? DEFAULT_ERROR_MESSAGE);
            }
            if (dataIndex === 'documentsSum' && (rowData?.documentsSum || 0) < (rowData?.documentsChecked || 0)) {
                errors.push(errorMessage ?? DEFAULT_ERROR_MESSAGE);
            }
            if (dataIndex === 'contractsSum' && (rowData?.contractsSum || 0) < (rowData?.contractsChecked || 0)) {
                errors.push(errorMessage ?? DEFAULT_ERROR_MESSAGE);
            }
        }

        if (mask === EEditableCellMaskValidation.errorWatcher) {
            errors.push(errorMessage ?? DEFAULT_ERROR_MESSAGE);
        }

        if (regExp) {
            const valueStr = value?.toString();
            if (valueStr && !valueStr.match(regExp)) {
                errors.push(errorMessage ?? DEFAULT_ERROR_MESSAGE);
            }
        }
    }

    return errors;
};

/**
 * Асинхронно валидирует всю строку данных формы, проверяя все редактируемые колонки
 *
 * @template TEntity - Тип сущности, представляющей строку данных
 * @param rowData - Данные текущей строки для валидации
 * @param rowIndex - Индекс строки в форме (для доступа к данным формы)
 * @param form - Экземпляр формы Ant Design (может быть undefined)
 * @param formListName - Имя Form.List, в котором находится строка
 * @param columns - Массив колонок таблицы с настройками валидации
 * @param entityName - Опциональное имя сущности (используется в валидаторах)
 * @returns Промис с объектом ошибок валидации, где ключ - имя поля, значение - массив сообщений об ошибках
 *
 * @remarks
 * Функция выполняет следующие действия:
 * 1. Собирает актуальные данные строки из формы (для работы с зависимыми полями)
 * 2. Для каждой редактируемой колонки запускает валидацию
 * 3. Выполняет вотчеры формы
 * 4. Совмещает статические и динамические(из вотчеров) правила валидации
 * 5. Возвращает агрегированные ошибки по всем полям строки
 *
 * @throws {Error} При внутренних ошибках выполнения валидации
 *
 * @see validateFieldValue - Функция валидации поля
 * @see executeFormWatchers - Функция выполнения вотчеров формы
 */
export const validateRowFull = async (
    rowData: TEntity,
    rowIndex: number,
    form: FormInstance | undefined,
    formListName: string,
    columns: TEditableColumn[],
    entityName?: string,
): Promise<Record<string, string[]>> => {
    const errors: Record<string, string[]> = {};

    const getFormValue = (path: (string | number)[]) => {
        const values = form?.getFieldsValue();
        return path.reduce((obj, key) => obj?.[key], values);
    };

    // Собираем все данные строки из формы для зависимых полей
    const rowFormData = getFormValue([formListName, rowIndex]) || rowData;

    // Валидация всех полей
    const validationPromises = columns.map(async (column) => {
        if (!column.editable || (!column.dataIndex && !column.key) || column.isHidden) return null;

        const fieldName = column.dataIndex as string || column.key;
        const value = rowData[fieldName] ?? rowFormData[fieldName];
        const {getFieldState} = column;

        const cellState = getFieldState ? {
            ...getDefaultFieldState(fieldName),
            ...getFieldState(fieldName, rowData),
            required: column.required || column.isRequired,
            dependencies: column?.dependentsFieldKey,
        } : {
            ...DEFAULT_STATE_CELL,
            required: column.required || column.isRequired,
            dependencies: column?.dependentsFieldKey,
        };

        if (!cellState?.activated) return null;

        const validationRules = column.validationRules || [];

        try {
            const {
                isRequired,
                validationRules: watcherValidationRules,
            } = executeFormWatchers(
                form?.getFieldsValue()[formListName][rowIndex],
                columns?.find(field => field.dataIndex === fieldName || field.key === fieldName) as IFieldMeta,
                form,
                EFormMode.EDIT,
                entityName,
            );

            const fieldErrors = await validateFieldValue(
                value,
                cellState,
                rowData,
                [...validationRules, ...(watcherValidationRules ?? [])],
                column.required || isRequired,
                fieldName,
                rowFormData,
            );

            if (fieldErrors.length > 0) {
                return {fieldName, errors: fieldErrors};
            }
        } catch (error) {
            return {fieldName, errors: ['Ошибка валидации']};
        }

        return null;
    });

    // Ждем завершения всех валидаций
    const results = await Promise.all(validationPromises);

    // Собираем ошибки
    results.forEach(result => {
        if (result) {
            errors[result.fieldName] = result.errors;
        }
    });
    if (Object.keys(errors).length > 0) console.error(`У строки ${rowIndex} ошибки валидации:`, errors);
    return errors;
};
